import { useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Box, Button, Chip, CircularProgress, Divider, Stack, Tooltip, Typography, IconButton,
} from '@mui/material'
import {
  Phone as PhoneIcon,
  PhoneCallback as IncomingIcon,
  PhoneForwarded as OutgoingIcon,
  AccessTime as DurationIcon,
  CalendarMonth as CalendarIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Download as DownloadIcon,
} from '@mui/icons-material'
import {
  callLogsApi, CallLog,
  CALL_STATUS_LABELS, CALL_STATUS_COLORS,
  CALL_OUTCOME_LABELS, CALL_OUTCOME_COLORS,
  formatDuration, formatCallTime, getCallStatusDetailText,
  hasValidRecordingUrl, downloadRecordingFile,
} from '../../lib/api/callLogsApi'

// ── Seekable waveform ─────────────────────────────────────────────────────────

const BARS = [3,6,9,5,12,8,4,10,7,14,6,9,4,11,7,3,8,12,5,9,4,7,10,5,8,3,6,11,7,4]

function SeekableWaveform({ progress = 0, onSeek }: { progress?: number; onSeek: (f: number) => void }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect) return
    onSeek(Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)))
  }
  return (
    <Box ref={containerRef} onClick={handleClick}
      sx={{ display: 'flex', alignItems: 'center', gap: '2px', height: 22, flex: 1, cursor: 'pointer' }}>
      {BARS.map((h, i) => (
        <Box key={i} sx={{
          width: 2, height: h, borderRadius: 1, flexShrink: 0,
          bgcolor: i / BARS.length <= progress ? '#7b1fa2' : '#9ca3af',
          opacity: i / BARS.length <= progress ? 1 : 0.6,
          transition: 'background-color 0.05s',
        }} />
      ))}
    </Box>
  )
}

// ── Sub-components ────────────────────────────────────────────────────────────

function CallLogRow({ call }: { call: CallLog }) {
  const isOutgoing = call.callType === 'OUTGOING'
  const hasOutcome = !!call.outcome
  const showPlayer = hasValidRecordingUrl(call.recordingUrl)

  const [isPlaying,  setIsPlaying]  = useState(false)
  const [progress,   setProgress]   = useState(0)
  const [currentSec, setCurrentSec] = useState(0)
  const [totalSec,   setTotalSec]   = useState<number>(call.durationSeconds ?? 0)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  useEffect(() => () => { audioRef.current?.pause(); audioRef.current = null }, [])

  const getAudio = (): HTMLAudioElement => {
    if (audioRef.current) return audioRef.current
    const audio = new Audio(call.recordingUrl!)
    audio.addEventListener('loadedmetadata', () => {
      if (isFinite(audio.duration)) setTotalSec(Math.round(audio.duration))
    })
    audio.addEventListener('timeupdate', () => {
      const dur = isFinite(audio.duration) && audio.duration > 0
        ? audio.duration : (call.durationSeconds ?? 1)
      setCurrentSec(Math.floor(audio.currentTime))
      setProgress(audio.currentTime / dur)
    })
    audio.addEventListener('ended', () => { setIsPlaying(false); setProgress(0); setCurrentSec(0) })
    audioRef.current = audio
    return audio
  }

  const handlePlayPause = () => {
    const audio = getAudio()
    if (isPlaying) { audio.pause(); setIsPlaying(false) }
    else { audio.play().catch(() => {}); setIsPlaying(true) }
  }

  const handleSeek = (fraction: number) => {
    const audio = getAudio()
    const dur = isFinite(audio.duration) && audio.duration > 0 ? audio.duration : (totalSec || 1)
    audio.currentTime = fraction * dur
    setProgress(fraction)
    setCurrentSec(Math.floor(fraction * dur))
  }

  const handleDownload = async () => {
    if (!hasValidRecordingUrl(call.recordingUrl)) return

    const fileName = (() => {
      const fromUrl = (call.recordingUrl ?? '').split('/').pop()?.split('?')[0]
      return fromUrl && fromUrl.includes('.') ? fromUrl : `call-recording-${call.id}.mp3`
    })()

    try {
      await downloadRecordingFile(call.recordingUrl, fileName)
    } catch {
      console.warn('No valid recording was available for download.')
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        px: 0.5,
        py: 1.25,
        alignItems: 'flex-start',
      }}
    >
      {/* Icon column */}
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: '10px',
          bgcolor: hasOutcome
            ? `${CALL_STATUS_COLORS[call.status]}15`
            : 'rgba(107,114,128,0.1)',
          border: `1px solid ${hasOutcome ? CALL_STATUS_COLORS[call.status] + '30' : 'rgba(0,0,0,0.06)'}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          mt: '2px',
        }}
      >
        {isOutgoing
          ? <OutgoingIcon sx={{ fontSize: 16, color: hasOutcome ? CALL_STATUS_COLORS[call.status] : '#9ca3af' }} />
          : <IncomingIcon sx={{ fontSize: 16, color: hasOutcome ? CALL_STATUS_COLORS[call.status] : '#9ca3af' }} />}
      </Box>

      {/* Details */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.4, flexWrap: 'wrap' }}>
          {/* Status chip */}
          <Chip
            label={CALL_STATUS_LABELS[call.status] ?? call.status}
            size="small"
            sx={{
              height: 18,
              fontSize: '10px',
              fontWeight: 700,
              bgcolor: `${CALL_STATUS_COLORS[call.status]}15`,
              color: CALL_STATUS_COLORS[call.status],
              border: `1px solid ${CALL_STATUS_COLORS[call.status]}30`,
              '& .MuiChip-label': { px: '5px' },
            }}
          />
          {/* Outcome chip */}
          {call.outcome && (
            <Chip
              label={CALL_OUTCOME_LABELS[call.outcome] ?? call.outcome}
              size="small"
              sx={{
                height: 18,
                fontSize: '10px',
                fontWeight: 700,
                bgcolor: `${CALL_OUTCOME_COLORS[call.outcome]}15`,
                color: CALL_OUTCOME_COLORS[call.outcome],
                border: `1px solid ${CALL_OUTCOME_COLORS[call.outcome]}30`,
                '& .MuiChip-label': { px: '5px' },
              }}
            />
          )}
          {/* Pending badge */}
          {!call.outcome && (
            <Chip
              label="Pending outcome"
              size="small"
              sx={{
                height: 18, fontSize: '10px', fontWeight: 700,
                bgcolor: '#fef3c7', color: '#92400e',
                border: '1px solid #fde68a',
                '& .MuiChip-label': { px: '5px' },
              }}
            />
          )}
          <Box sx={{ flex: 1 }} />
          {/* Type label */}
          <Typography fontSize="10px" color="text.disabled">
            {isOutgoing ? '↗ Outgoing' : '↙ Incoming'}
          </Typography>
        </Box>

        {/* Time + duration row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.4 }}>
          <Typography fontSize="11.5px" color="text.secondary">
            {formatCallTime(call.startedAt)}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
            <DurationIcon sx={{ fontSize: 11, color: '#9ca3af' }} />
            <Typography fontSize="11px" color="text.disabled">
              {formatDuration(call.durationSeconds)}
            </Typography>
          </Box>
        </Box>

        {/* Audio Player */}
        {showPlayer && (
          <Box sx={{ my: 1.25, p: 1.25, display: 'flex', alignItems: 'center', gap: 1.5,
            border: '1px solid #e5e7eb', borderRadius: '10px', bgcolor: '#fff', maxWidth: 480 }}>
            <IconButton size="small" onClick={handlePlayPause}
              sx={{ width: 30, height: 30, bgcolor: '#1f2937', color: '#fff', flexShrink: 0, '&:hover': { bgcolor: '#374151' } }}>
              {isPlaying
                ? <PauseIcon sx={{ fontSize: 16 }} />
                : <PlayIcon sx={{ fontSize: 16, ml: '2px' }} />}
            </IconButton>
            <SeekableWaveform progress={progress} onSeek={handleSeek} />
            <Typography fontSize="11px" color="text.secondary"
              sx={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums', minWidth: 60, textAlign: 'right' }}>
              {formatDuration(isPlaying || progress > 0 ? currentSec : null)} / {formatDuration(totalSec || call.durationSeconds)}
            </Typography>
            <Tooltip title="Download Recording">
              <IconButton size="small" onClick={handleDownload}
                sx={{ color: '#6b7280', '&:hover': { color: '#7b1fa2' }, flexShrink: 0 }}>
                <DownloadIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* Explanation status detail */}
        {(() => {
          const detailText = getCallStatusDetailText(call)
          if (!detailText) return null
          return (
            <Typography
              fontSize="11.5px"
              color="#b91c1c"
              sx={{ mt: 0.5, mb: 0.25, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              ⚠️ {detailText}
            </Typography>
          )
        })()}

        {/* Notes */}
        {call.notes && (
          <Typography
            fontSize="12px"
            color="text.secondary"
            sx={{
              mt: 0.25,
              fontStyle: 'italic',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            "{call.notes}"
          </Typography>
        )}

        {/* Follow-up date */}
        {call.followUpDate && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.5 }}>
            <CalendarIcon sx={{ fontSize: 12, color: '#0070d2' }} />
            <Typography fontSize="11px" color="#0070d2" fontWeight={600}>
              Follow-up: {new Date(call.followUpDate).toLocaleDateString('en-IN', {
                day: 'numeric', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit', hour12: true,
              })}
            </Typography>
          </Box>
        )}

        {/* Agent */}
        <Typography fontSize="10.5px" color="text.disabled" mt={0.5}>
          by {call.initiatedBy.name}
        </Typography>
      </Box>
    </Box>
  )
}

// ── Main export ───────────────────────────────────────────────────────────────

interface DealCallsSectionProps {
  dealId: string
  onLogCall: () => void
  canModify?: boolean
  hideToolbar?: boolean
}

export default function DealCallsSection({ dealId, onLogCall, canModify = true, hideToolbar }: DealCallsSectionProps) {
  const { data: calls = [], isLoading } = useQuery({
    queryKey: ['deal-call-logs', dealId],
    queryFn:  () => callLogsApi.getByDeal(dealId),
  })

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (calls.length === 0) {
    return (
      <Box>
        {canModify && !hideToolbar && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<PhoneIcon sx={{ fontSize: 15 }} />}
              onClick={onLogCall}
              sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: 13 }}
            >
              Log Call
            </Button>
          </Box>
        )}
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <PhoneIcon sx={{ fontSize: 36, color: 'rgba(0,0,0,0.1)', mb: 1, display: 'block', mx: 'auto' }} />
          <Typography fontSize="13px" color="text.secondary">No calls logged yet</Typography>
          {canModify && (
            <Typography fontSize="11.5px" color="text.disabled" mt={0.5}>
              Click "Log Call" to start a call for this deal
            </Typography>
          )}
        </Box>
      </Box>
    )
  }

  // Stats summary row
  const connected   = calls.filter(c => c.status === 'CONNECTED').length
  const notAnswered = calls.filter(c => c.status === 'NOT_ANSWERED' || c.status === 'BUSY').length
  const avgDuration = calls.filter(c => c.durationSeconds).length
    ? Math.round(calls.reduce((s, c) => s + (c.durationSeconds ?? 0), 0) / calls.filter(c => c.durationSeconds).length)
    : null

  return (
    <Box>
      {/* Toolbar */}
      {canModify && !hideToolbar && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<PhoneIcon sx={{ fontSize: 15 }} />}
            onClick={onLogCall}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: 13 }}
          >
            Log Call
          </Button>
        </Box>
      )}

      {/* Summary stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Chip
          label={`${calls.length} total`}
          size="small"
          sx={{ fontSize: '11px', bgcolor: '#f3f4f6', fontWeight: 600 }}
        />
        <Chip
          label={`${connected} connected`}
          size="small"
          sx={{ fontSize: '11px', bgcolor: '#dcfce7', color: '#166534', fontWeight: 600 }}
        />
        {notAnswered > 0 && (
          <Chip
            label={`${notAnswered} missed`}
            size="small"
            sx={{ fontSize: '11px', bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600 }}
          />
        )}
        {avgDuration && (
          <Chip
            label={`avg ${formatDuration(avgDuration)}`}
            size="small"
            sx={{ fontSize: '11px', bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 600 }}
          />
        )}
      </Box>

      {/* Call list */}
      <Stack divider={<Divider sx={{ borderColor: '#f3f4f6' }} />}>
        {calls.map(call => (
          <CallLogRow key={call.id} call={call} />
        ))}
      </Stack>
    </Box>
  )
}
